import { reactive } from 'vue'

/**
 * Client-side chat image upload. Two-step flow:
 *   1. POST /api/chat/upload/presign → { uploadUrl, fileUrl, key, mime, bytes }
 *   2. PUT file directly to S3 (uploadUrl is a 1h presigned URL)
 *
 * Tracks per-file state in a reactive map keyed by clientFileId (local uuid)
 * so Composer can show progress + cancel.
 */

export interface UploadEntry {
    clientFileId: string
    file: File
    localPreviewUrl: string    // object URL for in-browser preview
    progress: number           // 0..100
    state: 'queued' | 'uploading' | 'done' | 'error'
    error?: string
    // Populated on done:
    url?: string
    key?: string
    mime?: string
    bytes?: number
    width?: number
    height?: number
}

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif'])
const MAX_BYTES = 10 * 1024 * 1024

// Extension → MIME fallback for files that arrive without a `file.type`
// (drag-and-drop from Windows Explorer is a notable culprit). Without
// this lookup the allow-list check below rejects the file with the
// unhelpful "Unsupported file type" — but the underlying bytes are
// fine; just the metadata is missing.
const EXT_MIME: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
}

function inferMime(file: File): string {
    // file.type may be:
    //  - empty (drag-drop without type metadata)
    //  - mixed-case (`image/JPEG` from older browser polyfills)
    // Always normalise to lowercase; fall back to extension when empty.
    const raw = String(file.type || '').toLowerCase()
    if (raw) return raw
    const dot = file.name.lastIndexOf('.')
    if (dot < 0) return ''
    const ext = file.name.slice(dot + 1).toLowerCase()
    return EXT_MIME[ext] || ''
}

function makeClientFileId(): string {
    return 'cf_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
}

/** Detect image dimensions client-side before upload. Tiny, no deps. */
function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise(resolve => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight })
            URL.revokeObjectURL(url)
        }
        img.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(null)
        }
        img.src = url
    })
}

async function putToS3(uploadUrl: string, file: File, mime: string, onProgress: (p: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl, true)
        // Use the resolved (lowercased / inferred) MIME — the server
        // signed the presigned URL with this exact value, so the PUT
        // header MUST match or S3 returns 403 SignatureDoesNotMatch.
        // Using `file.type` raw was the source of the "image upload
        // silently fails" bug when browsers reported mixed-case types.
        xhr.setRequestHeader('Content-Type', mime)
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve()
            else reject(new Error(`s3_put_${xhr.status}`))
        }
        xhr.onerror = () => reject(new Error('s3_network_error'))
        xhr.send(file)
    })
}

export function useChatUpload() {
    // Reactive map of in-flight uploads; the Composer renders chips from here.
    const uploads = reactive(new Map<string, UploadEntry>())

    function validateFile(file: File): { error: string | null; mime: string } {
        const mime = inferMime(file)
        if (!mime) return { error: 'Unsupported file type', mime: '' }
        if (!ALLOWED_MIME.has(mime)) return { error: 'Unsupported file type', mime }
        if (file.size > MAX_BYTES) return { error: 'File too large (max 10 MB)', mime }
        return { error: null, mime }
    }

    async function uploadFile(file: File): Promise<UploadEntry> {
        const { error: err, mime: resolvedMime } = validateFile(file)
        const clientFileId = makeClientFileId()
        const localPreviewUrl = URL.createObjectURL(file)
        const entry: UploadEntry = {
            clientFileId,
            file,
            localPreviewUrl,
            progress: 0,
            state: err ? 'error' : 'queued',
            error: err || undefined,
        }
        uploads.set(clientFileId, entry)
        if (err) return entry

        try {
            entry.state = 'uploading'

            const dims = await readImageDimensions(file)
            if (dims) {
                entry.width = dims.width
                entry.height = dims.height
            }

            const presignRes = await $fetch<{
                uploadUrl: string; fileUrl: string; key: string; mime: string; bytes: number
            }>('/api/chat/upload/presign', {
                method: 'POST',
                // Send the RESOLVED mime (lowercased, extension-fallback)
                // so the server signs the upload with the same value the
                // PUT header will carry. Mismatched casing here was the
                // root cause of the silent "image upload failed" bug.
                body: { fileName: file.name, contentType: resolvedMime, bytes: file.size }
            })

            await putToS3(presignRes.uploadUrl, file, resolvedMime, (p) => { entry.progress = p })

            entry.url = presignRes.fileUrl
            entry.key = presignRes.key
            entry.mime = resolvedMime
            entry.bytes = file.size
            entry.progress = 100
            entry.state = 'done'
        } catch (e: any) {
            entry.state = 'error'
            entry.error = e?.data?.statusMessage || e?.message || 'upload_failed'
        }
        return entry
    }

    function remove(clientFileId: string) {
        const entry = uploads.get(clientFileId)
        if (entry?.localPreviewUrl) URL.revokeObjectURL(entry.localPreviewUrl)
        uploads.delete(clientFileId)
    }

    function clear() {
        for (const e of uploads.values()) {
            if (e.localPreviewUrl) URL.revokeObjectURL(e.localPreviewUrl)
        }
        uploads.clear()
    }

    /** Returns the attachment objects ready to send with a message, for
     *  completed uploads only. */
    function toAttachments(entries?: UploadEntry[]) {
        const list = entries || [...uploads.values()]
        return list
            .filter(e => e.state === 'done' && e.url && e.key && e.mime)
            .map(e => ({
                type: 'image' as const,
                url: e.url!,
                key: e.key!,
                mime: e.mime!,
                bytes: e.bytes,
                width: e.width,
                height: e.height
            }))
    }

    return { uploads, uploadFile, remove, clear, toAttachments, validateFile }
}
